
import Vue, {VueConstructor} from 'vue'
import FilterColumn from '~/components/filter/filter-column.vue'
import ChefCard from '~/components/index/chef-highlight/chef-card.vue'
import {updateQueryParams} from '~/util/query-parser'
import {mapGetters} from 'vuex'
import {SortingKey} from '~/store/filter'
import {find, findKey} from 'lodash'
import {SelectOption} from '~/components/ui/select.vue'
import {EVENT_SEARCH} from '~/util/event-bus'
import QueryMixin from '~/mixins/query.mixin'
import gtm from "~/plugins/gtm";
import moment from "moment/moment";

export default (Vue as VueConstructor<Vue & InstanceType<typeof QueryMixin>>).extend({
  name: 'SearchPage',
  mixins: [QueryMixin],
  components: {
    ChefCard,
    FilterColumn
  },
  head() {
    return {
      title: this.$data.seoTitle,
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: this.$data.seoDescriptionMeta,
        }
      ],
      bodyAttrs: {
        class: 'bg-white text-offBlack'
      },
    };
  },
  async mounted() {
    await this.$store.dispatch('filter/fetchFilters');

    let {
      address,
      calendar,
      person,
      page,
      dynamicFilters
    } = await this.parseQueryParams(this.$route.query as { [key: string]: string });

    // NOT ENABLED THIS, because issues with (Safari) when copying current page, results without URL params
    // this.canonicalURL = this.$route.path;
    // if (page > 1) {
    //   this.canonicalURL += '?page=' + page;
    // }

    const meta = this.$route.meta;
    if (meta && meta.type && meta.item) {
      dynamicFilters = this.findFilterByQueryParam(meta.type, meta.item, dynamicFilters);
    } else if (this.$route.params.type) {
      dynamicFilters = this.findFilterByQueryParam(this.$route.params.type, this.$route.params.item, dynamicFilters);
    }

    this.$store.commit('filter/UPDATE_FILTERS', {
      address,
      calendar,
      person,
      page,
      dynamicFilters
    })

    await this.fetchListings();

    this.$nuxt.$on(EVENT_SEARCH, () => {
      // This also calls queueRerender()
      this.updatePageIndex(1);
    });
  },
  computed: {
    ...mapGetters({
      chefs: 'filter/chefs',
      filters: 'filter/filter',
      filterData: 'filter/filterData',
      sortingKey: 'filter/sortingKey',
      pagination: 'filter/pagination'
    }),
    showMissingCoreFilter(): boolean {
      return !this.loading && this.showMissingCoreFilterHelp && this.filters.person === 0
    },
    totalChefs(): string {
      return (this.pagination?.total ?? 0).toString(10)
    },
    // Sort
    chefFilter: {
      get(): SelectOption | null {
        return find(this.sortOptions, {
          key: this.sortingKey
        }) ?? null
      },
      set(val: SelectOption | null) {
        this.$store.commit('filter/UPDATE_SORTING_KEY', val?.key ?? null)
        this.queueRerender()
      }
    }
  },
  watch: {
    "$route.query": "checkPageChange"
  },
  methods: {
    checkPageChange() {
      // Fix browser back for navigating between pages
      let routePage = parseInt(this.$route.query.page)
      if (isNaN(routePage)) {
        routePage = 1
      }

      if (routePage === this.filters.page) {
        return;
      }

      this.updatePageIndex(routePage);
    },
    openCoreFilters() {
      this.$nuxt.$emit('openAddressFilter');
    },
    resetFilters() {
      // todo should be method from filter-column
      this.$store.commit('filter/UPDATE_FILTERS', {
        dynamicFilters: {},
      });

      this.$store.commit('filter/UPDATE_SORTING_KEY', SortingKey.CHEF_SCORE);

      this.$nuxt.$emit(EVENT_SEARCH, {});
    },
    findFilterByQueryParam(slug: string, value: string, dynamicFilters: { [key: string]: number[] | number | string }) {
      const category = find(this.filterData?.categories, {
        slug
      });

      if (category !== undefined) {
        const found = find(category.features, {
          slug: value
        });

        const key = findKey(this.filterData?.categories, {
          slug
        }) as string

        this.title = found.name ?? this.title;
        this.seoTitle = found.seo_title ?? this.seoTitle;
        this.seoDescription = found.description ?? this.seoDescription;
        this.seoDescriptionFooter = found.description_footer ?? this.seoDescriptionFooter;
        this.seoDescriptionMeta = found.description_meta ?? this.seoDescriptionMeta;

        if (dynamicFilters[key]) {
          // @ts-ignore
          if (find(dynamicFilters[key], {key: found.id.toString(10)}) === undefined) {
            // @ts-ignore
            dynamicFilters[key].push({
              key: found.id.toString(),
              value: found.name ?? "no value"
            });
          }
        } else {
          // @ts-ignore
          dynamicFilters[key] = [{
            key: found.id.toString(),
            value: found.name
          }];
        }
      }

      return dynamicFilters;
    },
    updatePageIndex(index: number) {
      if (document) {
        document.getElementById('__nuxt')!.scrollIntoView({behavior: "smooth"});
      }

      this.$store.commit('filter/UPDATE_FILTERS', {
        ...this.filters,
        page: index
      })

      this.queueRerender()
    },
    queueRerender() {
      if (this.loading && !this.itemInQueue) {
        this.itemInQueue = true
      }

      if (this.timeOut) {
        clearTimeout(this.timeOut)
      }

      // @ts-ignore
      this.timeOut = setTimeout(async () => {
        await this.$router.push({
          query: updateQueryParams(this.filters)
        });

        await this.fetchListings()
      }, 500)
    },
    async fetchListings() {
      try {
        const gtmParams = {
          event: 'marketplace_search',
          address: '',
          date: '',
          dynamicFilters: this.filters.dynamicFilters,
          page: this.filters.page,
          guests: this.filters.person,
          sort: this.sortingKey,
        };

        if (this.filters.address) {
          gtmParams.address = this.filters.address
        }

        if (this.filters.calendar) {
          gtmParams.date = moment(this.filters.calendar).format('YYYY-MM-DD')
        }

        this.$gtm.push(gtmParams)
      } catch (e) {
        console.error('analytics', e)
      }

      try {
        this.loading = true
        await this.$store.dispatch('filter/fetchListings')
      } catch (e) {
        console.error('search', e)
      } finally {
        this.loading = false

        if (this.itemInQueue) {
          this.itemInQueue = false
          this.queueRerender()
        }
      }
    }
  },
  data() {
    return {
      title: 'Alle arrangementen',
      seoTitle: 'Bekijk alle arrangementen - CHEFSTBL',
      seoDescription: 'Op CHEFSTBL vind je jouw ideale kok op locatie, cateraar aan huis óf BBQ chef. Wat jij ook voor ogen hebt, bij ons organiseer je het! Boek bijvoorbeeld een prive chef  voor een verfijnd diner met de familie of een gezellig diner met de vriendengroep. Wil je een mijlpaal vieren met een grote groep? Kijk dan even bij de walking dinners en bufetten! Doe je dit toch liever buiten in de tuin in de vorm van een BBQ? In dat geval spiek je natuurlijk even bij onze BBQ chefs. Laat je inspireren door het grote scala arrangementen; van 4 gangen private dining tot 8 gangen streetfood of een overheerlijke tapas buffet of Italiaans buffet er is voor ieder wat wils. Niet alleen in soorten chefs en arrangementen, maar ook in soorten keukens kan de keuze niet op. Van Frans klassiek tot modern internationaal, bij ons vind je het allemaal. Dus neem snel een kijkje!',
      seoDescriptionFooter: 'Ons doel is om jou de zorgen uit handen te nemen. Tijdens het diner zelf, maar ook tijdens het plannen van het diner. Dit doen wij door het vinden en vergelijken van chefs makkelijker te maken, of het nu gaat om een kok op locatie, een BBQ chef of catering op locatie. Waar we echt het verschil maken is onze persoonlijke en eerlijke service. Weet je niet hoe het allemaal in z’n werk gaat? Kan je geen keuze maken tussen chefs? Heb je een vraag voor een chef of wil je gewoon even kennis met ons maken? Geen probleem, wij zijn altijd bereikbaar voor onze gasten. Want dat ben je bij CHEFSTBL; een gast, geen klant. Of je nu een speciaal moment wilt vieren, gewoon even wilt genieten met vrienden of een zakelijke lunch moet organiseren. Wij cheffen het!',
      seoDescriptionMeta: 'Op CHEFSTBL vind je jouw ideale kok op locatie, cateraar aan huis óf BBQ chef.',
      canonicalURL: '',
      timeOut: null as number | null,
      itemInQueue: false,
      readMoreTextOpen: false,
      showSearchHelp: true,
      showMissingCoreFilterHelp: true,
      mobileMenuOpen: false,
      loading: true,
      sortOptions: [
        {
          key: SortingKey.RECOMMENDED,
          value: 'Aanbevolen'
        },
        {
          key: SortingKey.PRICE,
          value: 'Prijs'
        },
        {
          key: SortingKey.DISTANCE,
          value: 'Afstand'
        },
        {
          key: SortingKey.CHEF_SCORE,
          value: 'Best beoordeeld'
        },
        {
          key: SortingKey.REVIEW_AMOUNT,
          value: 'Meeste reviews'
        },
        {
          key: SortingKey.NEWEST,
          value: 'Nieuwste'
        }
      ]
    }
  },
  beforeDestroy() {
    this.$nuxt.$off(EVENT_SEARCH)
  }
})
